(() => {
  'use strict';
  if (window.__monsterFavoritesReady) return;
  const configNode = document.querySelector('[data-mk-favorites-config]');
  if (!configNode) return;
  let config;
  try { config = JSON.parse(configNode.textContent); } catch { return; }
  window.__monsterFavoritesReady = true;
  const labels = config.labels, key = 'mk-favorites:v1';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const root = new URL(config.root || '/', location.origin).pathname.replace(/\/?$/, '/');
  const productHosts = new Map(), pages = new Map(), queue = [];
  let memory = [], persistent = true, running = 0;
  const text = (name, replacements = {}) => Object.entries(replacements).reduce((value, [token, replacement]) => value.replaceAll('[' + token + ']', String(replacement)), labels[name] || name);
  const safeImage = value => {
    if (!value) return '';
    try {
      const url = new URL(value, location.origin);
      return ['http:', 'https:'].includes(url.protocol) && (url.origin === location.origin || url.hostname === 'cdn.shopify.com' || url.hostname.endsWith('.shopify.com')) ? url.href : '';
    } catch { return ''; }
  };
  const normalize = values => {
    const result = new Map();
    if (!Array.isArray(values)) return [];
    values.slice(0, 200).forEach(value => {
      if (!value || !/^\d+$/.test(String(value.id)) || typeof value.handle !== 'string' || !/^[\p{L}\p{N}_-]+$/u.test(value.handle)) return;
      const entry = {id:String(value.id),handle:value.handle.slice(0, 255),title:String(value.title || value.handle).slice(0, 300),image:safeImage(value.image),savedAt:Number.isFinite(value.savedAt) ? value.savedAt : 0};
      result.set(entry.id, entry);
    });
    return [...result.values()];
  };
  const read = () => {
    if (!persistent) return memory;
    try {
      const raw = localStorage.getItem(key);
      try { memory = normalize(raw ? JSON.parse(raw) : []); } catch { memory = []; }
    } catch { persistent = false; }
    return memory;
  };
  const write = entries => {
    memory = normalize(entries);
    if (persistent) {
      try { localStorage.setItem(key, JSON.stringify(memory)); } catch { persistent = false; }
    }
    return persistent;
  };
  const legacy = (id, saved) => { try { localStorage.setItem('mk-saved-product:' + id, saved ? '1' : '0'); } catch {} };
  const productUrl = entry => root + 'products/' + encodeURIComponent(entry.handle);
  const countLabel = count => count === 1 ? text('one') : text('many', {count});
  const motion = (element, frames, duration = 260) => {
    if (reduced.matches || !element.isConnected) return null;
    return element.animate(frames, {duration,easing:'cubic-bezier(.2,.7,.2,1)'});
  };
  const money = amount => {
    try { return new Intl.NumberFormat(config.locale || 'en', {style:'currency',currency:config.currency,currencyDisplay:'code'}).format(amount / 100); }
    catch { return (config.currency || '') + ' ' + (amount / 100).toFixed(2); }
  };
  const notice = (page, message, undo = null) => {
    clearTimeout(page.noticeTimer);
    page.noticeAnimation?.cancel();
    const box = page.host.querySelector('[data-mk-favorites-notice]');
    box.querySelector('[data-favorite-notice-message]').textContent = message;
    box.querySelector('[data-favorite-undo]').hidden = !undo;
    page.undo = undo;
    if (box.hidden) { box.hidden = false; motion(box, [{opacity:0,transform:'translateY(5px)'},{opacity:1,transform:'translateY(0)'}]); }
    page.noticeTimer = setTimeout(() => {
      const finish = () => { box.hidden = true; page.undo = null; };
      const animation = motion(box, [{opacity:1},{opacity:0}], 200);
      page.noticeAnimation = animation;
      if (animation) animation.onfinish = finish; else finish();
    }, 7000);
  };
  const hydrate = async job => {
    const {page, card, entry} = job;
    if (page.cards.get(entry.id) !== card || !card.isConnected) return;
    const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 10000);
    const price = card.querySelector('[data-favorite-price]'), message = card.querySelector('[data-favorite-message]'), retry = card.querySelector('[data-favorite-retry]');
    retry.hidden = true; message.hidden = true;
    price.classList.add('is-loading'); price.setAttribute('aria-hidden', 'true'); price.textContent = '';
    try {
      const response = await fetch(productUrl(entry) + '.js', {credentials:'same-origin',signal:controller.signal,headers:{Accept:'application/json'}});
      if (!response.ok) throw new Error(response.status === 404 ? 'deleted' : 'failed');
      const product = await response.json();
      if (String(product.id) !== entry.id) throw new Error('deleted');
      if (page.cards.get(entry.id) !== card || !card.isConnected) return;
      const title = card.querySelector('[data-favorite-title]');
      title.textContent = product.title;
      card.querySelector('[data-favorite-remove]').setAttribute('aria-label', text('remove_label', {title:product.title}));
      const image = card.querySelector('[data-favorite-image]'), source = safeImage(product.featured_image);
      if (source) { if (image.getAttribute('src') !== source) image.src = source; image.hidden = false; image.alt = product.title; }
      const amount = Number(product.price_min ?? product.price);
      if (!Number.isFinite(amount) || amount < 0) throw new Error('failed');
      price.textContent = (product.price_varies ? text('from') + ' ' : '') + money(amount);
      price.removeAttribute('aria-hidden');
      const available = card.querySelector('[data-favorite-availability]');
      available.hidden = !!product.available; available.textContent = text('unavailable');
    } catch (error) {
      if (page.cards.get(entry.id) !== card || !card.isConnected) return;
      message.textContent = error.message === 'deleted' ? text('deleted') : text('failed');
      message.hidden = false; retry.hidden = error.message === 'deleted';
    } finally { clearTimeout(timer); price.classList.remove('is-loading'); }
  };
  const pump = () => {
    while (running < 4 && queue.length) {
      const job = queue.shift(); running++;
      hydrate(job).finally(() => { running--; pump(); });
    }
  };
  const schedule = (page, card, entry) => { queue.push({page,card,entry}); pump(); };
  const buildCard = (page, entry) => {
    const card = page.template.content.firstElementChild.cloneNode(true), url = productUrl(entry);
    card.dataset.favoriteId = entry.id;
    card.querySelectorAll('[data-favorite-image-link],[data-favorite-title],[data-favorite-view]').forEach(link => { link.href = url; });
    const title = card.querySelector('[data-favorite-title]'); title.textContent = entry.title;
    const imageLink = card.querySelector('[data-favorite-image-link]'); imageLink.setAttribute('aria-label', text('view') + ': ' + entry.title);
    const image = card.querySelector('[data-favorite-image]'); image.alt = entry.title;
    if (entry.image) image.src = entry.image; else image.hidden = true;
    image.addEventListener('error', () => { image.hidden = true; });
    const remove = card.querySelector('[data-favorite-remove]'); remove.setAttribute('aria-label', text('remove_label', {title:entry.title}));
    remove.addEventListener('click', () => {
      const saved = read().find(item => item.id === entry.id);
      if (!saved) return;
      const focused = card.contains(document.activeElement);
      write(read().filter(item => item.id !== entry.id)); legacy(entry.id, false); synchronize();
      if (focused) (page.grid.querySelector('[data-favorite-id]:not([aria-hidden="true"]) [data-favorite-title]') || page.host.querySelector('h1')).focus({preventScroll:true});
      notice(page, text('removed'), saved);
    });
    card.querySelector('[data-favorite-retry]').addEventListener('click', () => schedule(page, card, entry));
    return card;
  };
  const renderPage = (page, entries) => {
    if (!page.host.isConnected) { clearTimeout(page.noticeTimer); pages.delete(page.host); return; }
    const sort = page.sort.value;
    const ordered = [...entries].sort((a, b) => sort === 'name' ? a.title.localeCompare(b.title, config.locale) : sort === 'oldest' ? a.savedAt - b.savedAt : b.savedAt - a.savedAt);
    const wanted = new Set(ordered.map(entry => entry.id)), oldRects = new Map([...page.cards].map(([id, card]) => [id, card.getBoundingClientRect()]));
    const gridRect = page.grid.getBoundingClientRect();
    page.cards.forEach((card, id) => {
      if (wanted.has(id)) return;
      page.cards.delete(id);
      const rect = oldRects.get(id);
      if (reduced.matches) { card.remove(); return; }
      card.inert = true; card.setAttribute('aria-hidden', 'true');
      Object.assign(card.style, {position:'absolute',left:(rect.left - gridRect.left) + 'px',top:(rect.top - gridRect.top) + 'px',width:rect.width + 'px',height:rect.height + 'px',pointerEvents:'none',zIndex:'1'});
      const animation = motion(card, [{opacity:1,transform:'translateY(0)'},{opacity:0,transform:'translateY(-5px)'}], 220);
      if (animation) animation.onfinish = () => card.remove(); else card.remove();
    });
    const newCards = [];
    ordered.forEach(entry => {
      if (!page.cards.has(entry.id)) {
        const card = buildCard(page, entry); page.cards.set(entry.id, card); page.grid.append(card); newCards.push(card); schedule(page, card, entry);
      }
    });
    const activeOrder = [...page.grid.children].filter(card => !card.inert).map(card => card.dataset.favoriteId);
    if (activeOrder.join(',') !== ordered.map(entry => entry.id).join(',')) ordered.forEach(entry => page.grid.append(page.cards.get(entry.id)));
    page.host.querySelector('[data-mk-favorites-item-count]').textContent = countLabel(entries.length);
    page.host.querySelector('[data-mk-favorites-toolbar]').hidden = !entries.length;
    page.empty.hidden = !!entries.length;
    page.grid.setAttribute('aria-busy', 'false');
    if (!reduced.matches) requestAnimationFrame(() => {
      page.cards.forEach((card, id) => {
        card.getAnimations().forEach(animation => animation.cancel());
        const old = oldRects.get(id), rect = card.getBoundingClientRect();
        if (old && (Math.abs(old.left - rect.left) > 1 || Math.abs(old.top - rect.top) > 1)) motion(card, [{transform:`translate(${old.left - rect.left}px,${old.top - rect.top}px)`},{transform:'translate(0,0)'}], 300);
        else if (newCards.includes(card)) motion(card, [{opacity:0,transform:'translateY(5px)'},{opacity:1,transform:'translateY(0)'}], 300);
      });
    });
  };
  const synchronize = () => {
    const entries = read();
    document.querySelectorAll('[data-mk-favorites-count]').forEach(count => { count.textContent = String(entries.length); count.hidden = count.hasAttribute('data-hide-zero') && !entries.length; });
    document.querySelectorAll('[data-mk-favorites-link]').forEach(link => { link.setAttribute('aria-label', text('header_label', {count:entries.length,items:countLabel(entries.length)})); link.dataset.hasFavorites = String(entries.length > 0); });
    productHosts.forEach((record, host) => {
      if (!host.isConnected) { clearTimeout(record.timer); record.plusAnimation?.cancel(); record.heartAnimation?.cancel(); productHosts.delete(host); return; }
      const saved = entries.some(entry => entry.id === record.entry.id);
      record.button.disabled = false; record.button.setAttribute('aria-pressed', String(saved));
      record.button.setAttribute('aria-label', text(saved ? 'remove_label' : 'save_label', {title:record.entry.title}));
      const hasHistory = record.historicalCount !== null;
      if (record.count) record.count.textContent = String((hasHistory ? record.historicalCount : 0) + Number(saved));
      const description = hasHistory ? text('reported_history', {count:record.historicalCount}) + (saved ? ' ' + text('history_your_save') : '') : text(saved ? 'personal_saved' : 'personal_unsaved');
      if (record.history) { record.history.textContent = description; record.history.hidden = !hasHistory; }
      record.button.setAttribute('aria-description', description);
    });
    pages.forEach(page => renderPage(page, entries));
  };
  const find = (scope, selector) => [...(scope.matches?.(selector) ? [scope] : []), ...scope.querySelectorAll(selector)];
  const initialize = (scope = document) => {
    find(scope, '[data-mk-favorite-product]').forEach(host => {
      if (productHosts.has(host)) return;
      let entry, seed;
      try { seed = JSON.parse(host.querySelector('[data-mk-favorite-seed]').textContent); entry = normalize([seed])[0]; } catch { return; }
      if (!entry) return;
      const button = host.querySelector('[data-mk-favorite-toggle]'), feedback = host.querySelector('[data-mk-favorite-feedback]');
      const historicalCount = Number.isSafeInteger(seed.historicalCount) && seed.historicalCount >= 0 ? seed.historicalCount : null;
      const record = {entry,button,historicalCount,count:host.querySelector('[data-mk-product-favorites-count]'),history:host.querySelector('[data-mk-favorite-history]'),timer:null,feedbackAnimation:null}; productHosts.set(host, record);
      try {
        if (localStorage.getItem('mk-saved-product:' + entry.id) === '1' && !read().some(item => item.id === entry.id)) write([...read(), {...entry,savedAt:Date.now()}]);
      } catch {}
      button.addEventListener('click', () => {
        const entries = read(), saved = entries.some(item => item.id === entry.id);
        if (!saved && entries.length >= 200) { feedback.textContent = text('limit'); return; }
        const retained = write(saved ? entries.filter(item => item.id !== entry.id) : [{...entry,savedAt:Date.now()}, ...entries]);
        legacy(entry.id, !saved); synchronize();
        record.plusAnimation?.cancel(); record.heartAnimation?.cancel();
        const plus = host.querySelector('[data-mk-favorite-plus]'), heart = button.querySelector('svg');
        if (!saved && !reduced.matches) {
          if (plus) record.plusAnimation = motion(plus, [{opacity:0,transform:'translateY(3px)'},{opacity:1,transform:'translateY(-5px)',offset:.25},{opacity:1,transform:'translateY(-10px)',offset:.6},{opacity:0,transform:'translateY(-20px)'}], 850);
          if (heart) record.heartAnimation = motion(heart, [{transform:'scale(1)'},{transform:'scale(1.16)',offset:.4},{transform:'scale(1)'}], 480);
        }
        clearTimeout(record.timer); record.feedbackAnimation?.cancel(); feedback.textContent = !saved && !retained ? text('temporary') : text(saved ? 'removed' : 'added');
        motion(feedback, [{opacity:.35},{opacity:1}], 200);
        record.timer = setTimeout(() => {
          if (!host.isConnected) return;
          const animation = motion(feedback, [{opacity:1},{opacity:0}], 220);
          record.feedbackAnimation = animation;
          const finish = () => { feedback.textContent = ''; record.feedbackAnimation = null; };
          if (animation) animation.onfinish = finish; else finish();
        }, 6500);
      });
    });
    find(scope, '[data-mk-favorites-page]').forEach(host => {
      if (pages.has(host)) return;
      const page = {host,grid:host.querySelector('[data-mk-favorites-grid]'),empty:host.querySelector('[data-mk-favorites-empty]'),sort:host.querySelector('[data-mk-favorites-sort]'),template:host.querySelector('[data-mk-favorite-card]'),cards:new Map(),undo:null,noticeTimer:null};
      page.grid.style.position = 'relative';
      page.sort.addEventListener('change', () => renderPage(page, read()));
      host.querySelector('[data-favorite-undo]').addEventListener('click', () => {
        if (!page.undo) return;
        const entry = page.undo, entries = read();
        if (!entries.some(item => item.id === entry.id)) {
          if (entries.length >= 200) { notice(page, text('limit')); return; }
          write([entry, ...entries]);
        }
        legacy(entry.id, true); synchronize(); notice(page, text('undo_message'));
      });
      pages.set(host, page); document.title = text('title') + ' – ' + config.shop;
    });
    synchronize();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), {once:true}); else initialize();
  window.addEventListener('storage', event => { if (event.key === key || event.key === null) synchronize(); });
  window.addEventListener('pageshow', () => synchronize());
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  const observer = new MutationObserver(records => {
    records.forEach(record => record.addedNodes.forEach(node => {
      if (node.nodeType === 1 && (node.matches('[data-mk-favorite-product],[data-mk-favorites-page]') || node.querySelector('[data-mk-favorite-product],[data-mk-favorites-page]'))) initialize(node);
    }));
  });
  observer.observe(document.body, {childList:true,subtree:true});
})();
